import React, { useState, useContext, createContext, useRef } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import MenuManager from "../ConfigTabs/MenuManager";
import { IntegranteRotinas } from "./IntegranteRotinas";
import { IntegranteForm } from "./IntegranteForm";

// Context para gerenciar drawer
export const IntegranteTabsContext = createContext();

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`integrante-tabpanel-${index}`}
      aria-labelledby={`integrante-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `integrante-tab-${index}`,
    "aria-controls": `integrante-tabpanel-${index}`,
  };
}

export function IntegranteTabs() {
  const [value, setValue] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(false);
  const [currentDate, setCurrentDate] = useState(null);
  const [currentEditData, setCurrentEditData] = useState(null);
  const [onSaveCallback, setOnSaveCallback] = useState(null);
  const saveButtonRef = useRef(null);
  const isMobile = window.innerWidth <= 600;
  // const { user } = useContext(AuthContext); // Não utilizado

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const openStudentDrawer = (student, status, date, editData, onSave) => {
    setCurrentStudent(student);
    setCurrentStatus(status);
    setCurrentDate(date);
    setCurrentEditData(editData);
    setOnSaveCallback(() => onSave);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    // Limpar dados após um delay para permitir animação
    setTimeout(() => {
      setCurrentStudent(null);
      setCurrentStatus(false);
      setCurrentDate(null);
      setCurrentEditData(null);
      setOnSaveCallback(null);
    }, 300);
  };

  const handleSave = () => {
    if (onSaveCallback) {
      onSaveCallback();
    }
    closeDrawer();
  };

  return (
    <IntegranteTabsContext.Provider value={{ openStudentTab: openStudentDrawer }}>
      <Box sx={{ width: "100%", position: "relative" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="Rotinas do Integral"
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
            allowScrollButtonsMobile={isMobile}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "14px",
                fontWeight: "600",
                minHeight: "48px",
                color: "#666",
                "&.Mui-selected": {
                  color: "#1e958c",
                  fontWeight: "700",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1e958c",
              },
            }}
          >
            <Tab label="Rotinas" {...a11yProps(0)} />
            <Tab label="Gerenciar Cardápio" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <IntegranteRotinas />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <MenuManager />
        </TabPanel>

        {/* Drawer lateral para formulário do aluno */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={closeDrawer}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: "90%", md: "800px", lg: "900px" },
              maxWidth: "900px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            },
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#f8fafc",
            }}
          >
            {/* Header do Drawer */}
            <Box
              sx={{
                padding: "20px 24px",
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: 700,
                    color: "#1e958c",
                  }}
                >
                  {currentStudent?.Nome || "Rotina do Aluno"}
                </h2>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "14px",
                    color: "#666",
                  }}
                >
                  {currentStatus ? "Editar Rotina" : "Nova Rotina"}
                </p>
              </Box>
              <Button
                variant="contained"
                onClick={() => {
                  if (saveButtonRef.current) {
                    saveButtonRef.current();
                  }
                }}
                sx={{
                  backgroundColor: "#1e958c",
                  textTransform: "none",
                  fontWeight: 600,
                  padding: "8px 24px",
                  "&:hover": {
                    backgroundColor: "#0b4f6c",
                  },
                }}
              >
                {currentStatus ? "Alterar Rotina" : "Salvar Rotina"}
              </Button>
              <IconButton
                onClick={closeDrawer}
                sx={{
                  color: "#666",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Conteúdo do Drawer */}
            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                padding: "24px",
              }}
            >
              {currentStudent && (
                <IntegranteForm
                  student={currentStudent}
                  status={currentStatus}
                  date={currentDate}
                  editData={currentEditData}
                  onSave={handleSave}
                  onClose={closeDrawer}
                  onSaveClick={saveButtonRef}
                />
              )}
            </Box>
          </Box>
        </Drawer>
      </Box>
    </IntegranteTabsContext.Provider>
  );
}

